/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useCallback, useRef } from "react";

export interface FileInputOptions {
  accept?: string;
  multiple?: boolean;
  onFilesSelected?: (files: FileList) => void;
}

export function useFileInput(options: FileInputOptions = {}) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Destructure options to avoid dependency issues
  const { accept, multiple, onFilesSelected } = options;

  const openFileDialog = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const createFileInput = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    if (accept) {
      input.accept = accept;
    }
    input.multiple = multiple ?? false;
    input.style.display = "none";

    // Create a wrapper function to handle the event type conversion
    const handleChange = (event: Event): void => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0 && onFilesSelected) {
        onFilesSelected(target.files);
      }
      // Reset input value to allow selecting the same file again
      target.value = "";
    };

    input.addEventListener("change", handleChange);
    return input;
  }, [accept, multiple, onFilesSelected]);

  return {
    openFileDialog,
    createFileInput,
    inputRef,
  };
}
